import React, { FC } from 'react'
import { AnnotationFormValue, AnnotationsModalState } from '../../types'
import Select, { createFilter } from 'react-select'
import { CustomOption, MenuList } from '../CustomSelectOptions'
import { Spinner } from '@wpmedia/ads-spinner'
import intl from 'react-intl-universal'
import { AnnotationItem, AnnotationType } from '~/types/annotation'
import { validateAnnotationSquema } from '~/reactapp/shared/annotations/annotation.service'
import { isBoolean } from 'lodash'
import { createSelectOptions } from '../../editorUtils'

interface SelectFieldsProps {
  state: AnnotationsModalState
  setState: (state: any) => void
}

const SelectFields: FC<SelectFieldsProps> = ({ state, setState }) => {
  const itemsSelectRef = React.useRef<any>()
  const childrenSelectRef = React.useRef<any>()

  // Retrieve the information about the user annotation by calling the endpoint
  // ones the user has selected the annotation type.
  // (1) - OnChange func for the first select
  const onAnnotationTypeSelected = async (formValue) => {
    setState({
      categoryLoading: true,
      annotationItems: undefined,
      annotationChildren: undefined,
      schemaError: false,
      description: undefined,
      formTouched: false,
    })
    const annotationType = formValue as AnnotationFormValue<AnnotationType>
    const userAnnotationData = await validateAnnotationSquema(
      annotationType.value.link
    )

    if (!isBoolean(userAnnotationData)) {
      if (!userAnnotationData || userAnnotationData.items.length === 0) {
        setState({
          annotationItems: [],
          annotationFormData: { category: annotationType },
          description: undefined,
          categoryLoading: false,
          schemaError: false,
          formTouched: true,
        })
        return
      }
      const annotationItems = createSelectOptions(
        userAnnotationData.items,
        'title'
      )

      setState({
        annotationItems,
        annotationChildren: undefined,
        annotationFormData: { category: annotationType },
        description: undefined,
        categoryLoading: false,
        schemaError: false,
        formTouched: true,
      })
      // eslint-disable-next-line no-unused-expressions
      itemsSelectRef.current?.select?.clearValue()
    } else {
      setState({
        annotationItems: undefined,
        annotationChildren: undefined,
        annotationFormData: {},
        description: undefined,
        categoryLoading: false,
        schemaError: true,
        formTouched: false,
      })
    }
  }

  // (2) - OnChange func for the second select
  const onItemSelected = async (formValue) => {
    if (!formValue) return
    const item = formValue as AnnotationFormValue<AnnotationItem>
    // Evaluate if the item has children. If not, set the description
    if (
      !item.value.children ||
      (item.value.children && item.value.children.length === 0)
    ) {
      setState({
        description: item.value.description ?? '',
        annotationChildren: undefined,
        annotationFormData: {
          category: state.annotationFormData.category,
          item: item,
        },
        formTouched: true,
      })
      return
    }
    const annotationChildren = createSelectOptions(item.value.children, 'title')
    setState({
      annotationChildren,
      annotationFormData: {
        category: state.annotationFormData.category,
        item: item,
      },
      description: undefined,
      formTouched: true,
    })
    // eslint-disable-next-line no-unused-expressions
    childrenSelectRef.current?.select?.clearValue()
  }

  // (3) - OnChange func for the tirdth select
  const onChildSelected = async (formValue) => {
    const child = formValue as AnnotationFormValue<AnnotationItem>
    if (!child) return
    setState({
      annotationFormData: {
        ...state.annotationFormData,
        child: child,
      },
      description: child.value.description ?? '',
      formTouched: true,
    })
  }

  const selectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      border: '1px solid #656A86',
      borderRadius: '0',
    }),
  }

  return (
    <div className="annotation-modal-select-fields">
      <Select
        isSearchable={false}
        options={state.annotationTypes}
        styles={selectStyles}
        className="annotation-modal-select-field"
        onChange={onAnnotationTypeSelected}
        placeholder={intl.get('annotationPlugin.modal.select.selectCategory')}
        value={state.annotationFormData.category}
        id="annotation-modal-select-category"
        isDisabled={state.categoryLoading}
      />
      {state.categoryLoading && (
        <div className="mt-3">
          <Spinner />
        </div>
      )}
      {/* The following "select" has been built taking into account that it
        will handle a lot of data, and we needed to perform its behaviour */}
      {state.annotationItems && (
        <Select
          filterOption={createFilter({ ignoreAccents: false })}
          isSearchable={true}
          options={state.annotationItems}
          styles={selectStyles}
          className="annotation-modal-select-field"
          components={{ MenuList, Option: CustomOption }}
          onChange={onItemSelected}
          ref={itemsSelectRef}
          value={state.annotationFormData.item}
          placeholder={intl.get(
            'annotationPlugin.modal.select.selectDynamicPlaceholder',
            { field: state.annotationFormData.category?.label }
          )}
          id="annotation-modal-select-item"
          noOptionsMessage={() =>
            intl.get('annotationPlugin.modal.select.noOptions')
          }
        />
      )}
      {state.annotationChildren && state.annotationChildren?.length > 0 && (
        <Select
          isSearchable={true}
          options={state.annotationChildren}
          styles={selectStyles}
          className="annotation-modal-select-field"
          ref={childrenSelectRef}
          onChange={onChildSelected}
          classNamePrefix="custom-select"
          value={state.annotationFormData.child}
          placeholder={intl.get(
            'annotationPlugin.modal.select.selectDynamicPlaceholder',
            { field: state.annotationFormData.item?.label }
          )}
          id="annotation-modal-select-child"
          noOptionsMessage={() =>
            intl.get('annotationPlugin.modal.select.noOptions')
          }
        />
      )}
      {state.schemaError && (
        <div id="annotation-modal-schema-error">
          {intl.get('annotationPlugin.modal.schemaError')}
        </div>
      )}
      {state.description && (
        <div className="annotation-modal-description">
          <div className="annotation-modal-description-title">
            {state.annotationFormData.item?.value.title}
            {state.annotationFormData.child
              ? ' - ' + state.annotationFormData.child.value.title
              : ''}
          </div>
          <div
            id="annotation-modal-description-content"
            dangerouslySetInnerHTML={{
              __html: state.description,
            }}
          />
        </div>
      )}
    </div>
  )
}

export default SelectFields
